import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    getAllChildren,
    getFlexDirection,
    type AstView,
    type EnrichedAstNode,
    findAncestors,
    findNodeById,
    findParentOfNodeById,
} from 'rendition-core';
import {uniq} from 'lodash';

import {useHighlighterContext} from 'Contexts/HighlighterContext';
import {usePreviewContext} from 'Contexts/PreviewContext';
import Button from 'Components/Tailwind/Button';
import {EditableNodeName} from 'Components/EditableNodeName';
import {Chevron} from 'Icons/Chevron';
import TripleDot from 'Icons/TripleDot';
import Pin from 'Icons/Pin';
import classNames from 'classnames';
import {FlexIcon} from 'Icons/FlexIcon';
import {TextIcon} from 'Icons/TextIcon';
import {ImageIcon} from 'Icons/ImageIcon';
import {HotkeyTooltip} from '../HotkeyTooltip';
import {SaveStatus} from '../SaveStatus';
import {DndProvider} from 'react-dnd';
import {HTML5Backend} from 'react-dnd-html5-backend';
import Layer from './Layer';
import {useViewContext} from 'Contexts/ViewContext';

interface Props {
    view: AstView;
    setView: (view: AstView) => void;
    gptRename: () => void;
    gptRenameLoading: boolean;
    jumpToEditNextNode: () => void;
    selectedNodeIds: Array<string>;
    saveStatus: 'saved' | 'saving' | 'unsaved';
}

const NodeIconMap: Record<EnrichedAstNode['type'], React.ReactNode | null> = {
    Instance: <FlexIcon />,
    Frame: <FlexIcon />,
    Text: <TextIcon />,
    Image: <ImageIcon />,
    Error: null,
    List: null,
};

export const GroupingTree: React.FC<React.PropsWithChildren<Props>> = ({
    view,
    selectedNodeIds,
    gptRename,
    gptRenameLoading,
    saveStatus,
    setView,
    jumpToEditNextNode,
}) => {
    const node = view.tree;
    // usePreview context.
    const {setHiddenNodes, hiddenNodes} = usePreviewContext();
    const selectedNode = useMemo(
        () => (selectedNodeIds.length === 1 ? findNodeById(view.tree, selectedNodeIds[0]) : undefined),
        [selectedNodeIds, view.tree]
    );

    const [contextuallyExpandedNodes, setContextuallyExpandedNodes] = useState<Array<string>>([node.internalId]);
    const [forceCollapsedNodes, setForceCollapsedNodes] = useState<Array<string>>([]);
    const [expandedNodes, setExpandedNodes] = useState<Array<string>>([]);

    const selectedNodeAncestors = useMemo(
        () => (selectedNode ? findAncestors(selectedNode, view.tree) : []),
        [selectedNode, view.tree]
    );

    useEffect(() => {
        if (selectedNode) {
            setContextuallyExpandedNodes(uniq([...contextuallyExpandedNodes, ...selectedNodeAncestors]));
            setForceCollapsedNodes(forceCollapsedNodes.filter((id) => !selectedNodeAncestors.includes(id)));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedNode]);

    return (
        <div className="absolute bottom-2 left-2 top-14 z-[998] flex w-60 flex-col rounded-lg bg-zinc-900">
            <div className="align-center sticky left-0 right-0 top-0 z-20 flex w-full flex-row justify-between rounded-tl-lg rounded-tr-lg pb-6 pl-4 pt-4">
                <h1 className="text-sm font-bold text-white">Layers</h1>
                <HotkeyTooltip />
            </div>
            <div className="relative flex h-full flex-col overflow-y-auto px-2 pb-12">
                <DndProvider backend={HTML5Backend}>
                    <NodeText
                        view={view}
                        setView={setView}
                        isRoot
                        node={node}
                        contextuallyExpandedNodes={contextuallyExpandedNodes}
                        setContextuallyExpandedNodes={setContextuallyExpandedNodes}
                        forceCollapsedNodes={forceCollapsedNodes}
                        setForceCollapsedNodes={setForceCollapsedNodes}
                        expandedNodes={expandedNodes}
                        setExpandedNodes={setExpandedNodes}
                        jumpToEditNextNode={jumpToEditNextNode}
                        hiddenNodes={hiddenNodes}
                        setHiddenNodes={setHiddenNodes}
                        depth={0}
                    />
                </DndProvider>
            </div>
            <Button
                className={classNames('mb-14 flex w-full gap-2 rounded-lg bg-zinc-800')}
                onClick={gptRename}
                disabled={gptRenameLoading}
            >
                {gptRenameLoading ? 'Renaming...' : 'Rename with GPT'}{' '}
            </Button>

            <SaveStatus saveStatus={saveStatus} />
        </div>
    );
};

interface NodeTextProps {
    view: AstView;
    setView: (view: AstView) => void;
    node: EnrichedAstNode;
    parentId?: string;
    collapsed?: boolean;
    contextuallyExpandedNodes: Array<string>;
    setContextuallyExpandedNodes: (nodes: Array<string>) => void;
    forceCollapsedNodes: Array<string>;
    setForceCollapsedNodes: (nodes: Array<string>) => void;
    expandedNodes: Array<string>;
    setExpandedNodes: (nodes: Array<string>) => void;
    jumpToEditNextNode: () => void;
    setHiddenNodes: (nodes: Array<string>) => void;
    hiddenNodes: Array<string>;
    isRoot?: boolean;
    depth?: number;
    isChildOfSelectedNode?: boolean;
}

const NodeText: React.FC<React.PropsWithChildren<NodeTextProps>> = ({
    view,
    setView,
    node,
    contextuallyExpandedNodes,
    setContextuallyExpandedNodes,
    forceCollapsedNodes,
    setForceCollapsedNodes,
    expandedNodes,
    setExpandedNodes,
    collapsed,
    jumpToEditNextNode,
    setHiddenNodes,
    hiddenNodes,
    depth = 0,
    isRoot = false,
    isChildOfSelectedNode = false,
}) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const {highLightedNodes} = useHighlighterContext();
    const {onNodeClick, onNodeMouseEnter, onNodeMouseLeave, onNodeRightClick} = usePreviewContext();
    const {updateView, selectedNodeIds} = useViewContext();

    const isAbsolutePositioned = node.inputLayoutAbstraction.absolutePosition || node.styles.position === 'absolute';
    const isHovered = highLightedNodes[node.internalId]?.includes('Hover');
    const isSelected = selectedNodeIds.includes(node.internalId);

    const moveLayer = useCallback(
        (fromNodeId: string, toNodeId: string, position: 'top' | 'bottom' | 'middle' | null) => {
            updateView((currentView: AstView) => {
                if (fromNodeId === currentView.tree.internalId) {
                    // Ignore rootnode being dragged.
                    return currentView;
                }
                const fromNode = findNodeById(currentView.tree, fromNodeId)!;
                const toNode = findNodeById(currentView.tree, toNodeId)!;

                // Find the parent nodes
                const fromParentNode = findParentOfNodeById(currentView.tree, fromNode.internalId)!;

                // Remove the node from the original parent's children array
                const fromParentChildren = fromParentNode.children;
                fromParentChildren.splice(
                    fromParentChildren.findIndex((d) => d.internalId === fromNode.internalId),
                    1
                );

                // force root nodes to fall in middle to avoid crash.
                if (toNode.internalId === currentView.tree.internalId) {
                    position = 'middle';
                }

                // Drop the node into the target node's children
                if (position === 'middle' && toNode.type === 'Frame') {
                    toNode.children.splice(0, 0, fromNode);
                } else {
                    const toParentNode = findParentOfNodeById(currentView.tree, toNode.internalId)!;
                    const toParentChildren = toParentNode.children;
                    const index = toParentChildren.findIndex((d) => d.internalId === toNode.internalId);
                    if (position === 'top') {
                        toParentChildren.splice(index, 0, fromNode);
                    } else {
                        toParentChildren.splice(index + 1, 0, fromNode);
                    }
                }

                // Call setView to update the view
                return currentView;
            });
        },
        [updateView]
    );

    useEffect(() => {
        // If this is the first selected node, scroll to the center of the container
        if (ref.current && selectedNodeIds[0] === node.internalId) {
            ref.current.scrollIntoView({behavior: 'smooth'});
        }
    }, [node.internalId, selectedNodeIds]);

    const allChildren = useMemo(() => getAllChildren(node), [node]);
    const isExpanded = useMemo(
        () =>
            (contextuallyExpandedNodes.includes(node.internalId) || expandedNodes.includes(node.internalId)) &&
            !forceCollapsedNodes.includes(node.internalId),
        [contextuallyExpandedNodes, expandedNodes, forceCollapsedNodes, node.internalId]
    );

    const hasHoveredChild = useMemo(
        () => allChildren.find((child) => highLightedNodes[child]?.includes('Hover')),
        [allChildren, highLightedNodes]
    );

    const onClick = useCallback(() => {
        if (isExpanded) {
            setForceCollapsedNodes([...forceCollapsedNodes, node.internalId]);
            setExpandedNodes(expandedNodes.filter((id) => id !== node.internalId));

            return;
        }
        setForceCollapsedNodes(forceCollapsedNodes.filter((id) => id !== node.internalId));
        setExpandedNodes([...expandedNodes, node.internalId]);
    }, [isExpanded, setForceCollapsedNodes, forceCollapsedNodes, setExpandedNodes, expandedNodes, node.internalId]);

    if (collapsed) {
        return null;
    }

    const tabSize = 20 * depth;
    const isHighlighted = isSelected || isChildOfSelectedNode;
    const isHoverHighlighted = (!isExpanded && hasHoveredChild) || isHovered;

    return (
        <>
            <Layer key={`layer-tree ${node.internalId}`} node={node} moveLayer={moveLayer}>
                <div
                    id={isRoot ? 'rendition-layers' : ''}
                    className={classNames(
                        `flex h-9 w-full flex-row items-center justify-start bg-none px-1 py-2 text-white`,
                        isChildOfSelectedNode && 'bg-opacity-40',
                        !isHighlighted && !isHoverHighlighted && 'hover:bg-sky-400 hover:bg-opacity-10',
                        isHighlighted && 'bg-sky-400',
                        !isHighlighted && isHoverHighlighted && 'bg-sky-400 bg-opacity-20'
                    )}
                    ref={ref}
                    onMouseEnter={(e) => onNodeMouseEnter(e, node.internalId)}
                    onMouseLeave={(e) => onNodeMouseLeave(e, node.internalId)}
                    onContextMenu={(e) => onNodeRightClick(e, node.internalId)}
                    onMouseDown={(e) => onNodeClick(e, node.internalId)}
                >
                    <div style={{width: tabSize}}></div>
                    <div
                        className="inline-flex flex-row items-center justify-between"
                        style={{width: `calc(100% - ${tabSize}px)`}}
                    >
                        <div className="inline-flex w-10/12 items-center justify-start">
                            <Chevron
                                className="mr-2 p-0.5"
                                width={18}
                                height={18}
                                direction={isExpanded ? 'down' : 'left'}
                                style={{
                                    cursor: 'pointer',
                                    minWidth: 18,
                                    visibility: node.type !== 'Text' && node.children.length ? 'visible' : 'hidden',
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onClick();
                                }}
                            />
                            {!isRoot && NodeIconMap[node.type] && (
                                <div
                                    className={classNames(
                                        'mr-2 flex items-center justify-center',
                                        node.type === 'Frame' || node.type === 'Instance'
                                            ? 'fill-stone-500'
                                            : 'stroke-stroke-500 fill-stone-300/10',
                                        (node.type === 'Frame' || node.type === 'Instance') &&
                                            getFlexDirection(node) === 'column' &&
                                            'rotate-90'
                                    )}
                                >
                                    {NodeIconMap[node.type]}
                                </div>
                            )}
                            <div
                                className={classNames(
                                    isAbsolutePositioned ? 'max-w-[55%]' : depth > 2 ? 'max-w-[65%]' : 'max-w-[75%]',
                                    'w-full'
                                )}
                            >
                                <EditableNodeName
                                    node={node}
                                    updateView={() => setView({...view})}
                                    jumpToEditNextNode={jumpToEditNextNode}
                                />
                            </div>
                            {isAbsolutePositioned && <Pin className="ml-2 stroke-neutral-300" />}
                        </div>

                        <button
                            type="button"
                            className={`inline-flex h-7 w-7 items-center justify-center rounded-full p-2.5 hover:bg-white hover:bg-opacity-5 hover:fill-white ${
                                isSelected || isHovered ? 'visible' : 'invisible'
                            }`}
                            onClick={(e) => onNodeRightClick(e, node.internalId)}
                        >
                            <TripleDot />
                            <span className="sr-only">Node Actions</span>
                        </button>
                    </div>
                </div>
                {node.children.map((child) => (
                    <NodeText
                        view={view}
                        setView={setView}
                        key={child.internalId}
                        node={child}
                        collapsed={!isExpanded}
                        setContextuallyExpandedNodes={setContextuallyExpandedNodes}
                        setForceCollapsedNodes={setForceCollapsedNodes}
                        forceCollapsedNodes={forceCollapsedNodes}
                        contextuallyExpandedNodes={contextuallyExpandedNodes}
                        expandedNodes={expandedNodes}
                        setExpandedNodes={setExpandedNodes}
                        jumpToEditNextNode={jumpToEditNextNode}
                        setHiddenNodes={setHiddenNodes}
                        hiddenNodes={hiddenNodes}
                        depth={depth + 1}
                        isChildOfSelectedNode={isSelected || isChildOfSelectedNode}
                    />
                ))}
            </Layer>
        </>
    );
};
